import { db } from './firebase';
import { 
  doc, getDoc, setDoc, updateDoc, collection,
  query, orderBy, getDocs, addDoc, serverTimestamp, limit as limitFirestore, where, arrayUnion, Timestamp 
} from 'firebase/firestore';

// Initialize a new user's wallets
export const initializeUserWallets = async (uid) => {
  const userRef = doc(db, 'users', uid);
  
  try {
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      await setDoc(userRef, {
        walletA: 0, // Earned via blogs/referrals, used for entry
        walletB: 0, // Winnings from predictions, used for withdrawal
        ads: 0,
        referrals: 0,
        login: 0,
        accuracyBonus: 0
      });
    } else {
      // Update existing user document with wallet fields if they don't exist
      const userData = userDoc.data();
      const updates = {};
      
      if (userData.walletA === undefined) updates.walletA = 0;
      if (userData.walletB === undefined) updates.walletB = 0;
      if (userData.ads === undefined) updates.ads = 0;
      if (userData.referrals === undefined) updates.referrals = 0;
      if (userData.login === undefined) updates.login = 0;
      if (userData.accuracyBonus === undefined) updates.accuracyBonus = 0;
      
      if (Object.keys(updates).length > 0) {
        await updateDoc(userRef, updates);
      }
    }
    
    return true;
  } catch (error) {
    console.error("Error initializing wallets:", error);
    return false;
  }
};

// Get user wallet balances
export const getWalletBalances = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        walletA: userData.walletA || 0,
        walletB: userData.walletB || 0
      };
    } else {
      // Initialize wallets if they don't exist
      await initializeUserWallets(uid);
      return { walletA: 0, walletB: 0 };
    }
  } catch (error) {
    console.error("Error getting wallet balances:", error);
    return { walletA: 0, walletB: 0 };
  }
};

// Get user wallet sources
export const getWalletSources = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        ads: userData.ads || 0,
        referrals: userData.referrals || 0,
        login: userData.login || 0,
        accuracyBonus: userData.accuracyBonus || 0
      };
    } else {
      // Initialize sources if they don't exist
      await initializeUserWallets(uid);
      return { ads: 0, referrals: 0, login: 0, accuracyBonus: 0 };
    }
  } catch (error) {
    console.error("Error getting wallet sources:", error);
    return { ads: 0, referrals: 0, login: 0, accuracyBonus: 0 };
  }
};

// Update wallet balance
export const updateWalletBalance = async (uid, wallet, amount) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const currentBalance = userData[wallet] || 0;
      const newBalance = currentBalance + amount;
      
      // Prevent negative balance
      if (newBalance < 0) {
        throw new Error("Insufficient balance");
      }
      
      await updateDoc(userRef, {
        [wallet]: newBalance
      });
      
      return newBalance;
    } else {
      throw new Error("User not found");
    }
  } catch (error) {
    console.error(`Error updating ${wallet}:`, error);
    throw error;
  }
};

// Update source balance
export const updateSourceBalance = async (uid, source, amount) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const currentBalance = userData[source] || 0;
      const newBalance = currentBalance + amount;
      
      // Prevent negative balance
      if (newBalance < 0) {
        throw new Error("Insufficient balance");
      }
      
      await updateDoc(userRef, {
        [source]: newBalance
      });
      
      return newBalance;
    } else {
      throw new Error("User not found");
    }
  } catch (error) {
    console.error(`Error updating ${source} source:`, error);
    throw error;
  }
};

// Create a transaction record
export const createTransaction = async (uid, type, amount, source = null) => {
  try {
    const transactionsRef = collection(db, 'users', uid, 'transactions');
    
    await addDoc(transactionsRef, {
      type,
      amount,
      source,
      timestamp: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error("Error creating transaction:", error);
    return false;
  }
};

// Get recent transactions
export const getRecentTransactions = async (uid, limitCount = 10) => {
  try {
    const transactionsRef = collection(db, 'users', uid, 'transactions');
    const q = query(transactionsRef, orderBy('timestamp', 'desc'), limitFirestore(limitCount));
    
    const querySnapshot = await getDocs(q);
    const transactions = [];
    
    querySnapshot.forEach((doc) => {
      transactions.push({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      });
    });
    
    return transactions;
  } catch (error) {
    console.error("Error getting transactions:", error);
    return [];
  }
};

// Helper functions for common wallet operations
export const earnToWalletA = async (uid, amount, source = 'ads') => {
  try {
    // Update wallet A balance
    await updateWalletBalance(uid, 'walletA', amount);
    
    // Update source balance
    if (source) {
      await updateSourceBalance(uid, source, amount);
    }
    
    // Create transaction record
    await createTransaction(uid, 'earn', amount, source);
    
    return true;
  } catch (error) {
    console.error("Error earning to wallet A:", error);
    return false;
  }
};

export const spendFromWalletA = async (uid, amount) => {
  try {
    await updateWalletBalance(uid, 'walletA', -amount);
    await createTransaction(uid, 'spend', -amount);
    return true;
  } catch (error) {
    console.error("Error spending from wallet A:", error);
    return false;
  }
};

export const winToWalletB = async (uid, amount) => {
  try {
    await updateWalletBalance(uid, 'walletB', amount);
    await createTransaction(uid, 'win', amount);
    return true;
  } catch (error) {
    console.error("Error winning to wallet B:", error);
    return false;
  }
};

export const withdrawFromWalletB = async (uid, amount) => {
  try {
    await updateWalletBalance(uid, 'walletB', -amount);
    await createTransaction(uid, 'withdraw', -amount);
    return true;
  } catch (error) {
    console.error("Error withdrawing from wallet B:", error);
    return false;
  }
};

// Create a withdrawal request
export const createWithdrawalRequest = async (uid, amount, paymentMethod, accountDetails) => {
  try {
    const withdrawalsRef = collection(db, 'withdrawals');
    
    // Create the withdrawal request
    await addDoc(withdrawalsRef, {
      userId: uid,
      amount: amount / 100, // Convert from coins to rupees
      paymentMethod: paymentMethod || 'UPI',
      accountDetails: accountDetails,
      status: 'pending',
      paymentStatus: 'unpaid',
      timestamp: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error("Error creating withdrawal request:", error);
    return false;
  }
};

// Convert coins from Wallet B to Wallet A
export const convertWalletBToA = async (uid, amount) => {
  try {
    // Deduct from wallet B
    await updateWalletBalance(uid, 'walletB', -amount);
    // Add to wallet A
    await updateWalletBalance(uid, 'walletA', amount);
    // Create transaction record
    await createTransaction(uid, 'convert', amount);
    return true;
  } catch (error) {
    console.error("Error converting from wallet B to A:", error);
    return false;
  }
};

// Convert coins from Wallet A to Wallet B (only from ad-earned coins)
export const convertWalletAToB = async (uid, amount) => {
  try {
    // Check if user has enough ad-earned coins
    const sources = await getWalletSources(uid);
    if (sources.ads < amount) {
      throw new Error("Insufficient ad-earned coins for conversion");
    }
    
    // Deduct from wallet A
    await updateWalletBalance(uid, 'walletA', -amount);
    
    // Deduct from ads source
    await updateSourceBalance(uid, 'ads', -amount);
    
    // Add to wallet B
    await updateWalletBalance(uid, 'walletB', amount);
    
    // Create transaction record
    await createTransaction(uid, 'convert_to_b', amount, 'ads');
    
    return true;
  } catch (error) {
    console.error("Error converting from wallet A to B:", error);
    return false;
  }
};

// Add daily login reward (with fixed reward amount)
export const addDailyLoginReward = async (uid) => {
  try {
    // First check if the user is eligible to claim
    const isEligible = await checkDailyRewardEligibility(uid);
    if (!isEligible) {
      console.log(`User ${uid} is not eligible for daily login reward`);
      return { success: false, message: "Already claimed today" };
    }
    
    // Fixed reward amount of 200 coins
    const rewardAmount = 200;
    
    // Prepare a result object
    const result = {
      success: false,
      amount: rewardAmount,
      streak: 1
    };
    
    try {
      // Update wallet A balance
      await updateWalletBalance(uid, 'walletA', rewardAmount);
      
      // Update login source balance
      await updateSourceBalance(uid, 'login', rewardAmount);
      
      // Create transaction record
      await createTransaction(uid, 'login_reward', rewardAmount, 'login');
      
      // Try to update streak info, but don't let it fail the entire process
      try {
        // Get the user's current streak
        const { currentStreak, highestStreak } = await getLoginStreak(uid);
        await updateLoginStreak(uid, currentStreak + 1, Math.max(currentStreak + 1, highestStreak));
        result.streak = currentStreak + 1;
      } catch (streakError) {
        console.error("Error updating streak data, continuing anyway:", streakError);
        // We'll just use a default streak of 1 since we couldn't get the real value
      }
      
      console.log(`Daily login reward of ${rewardAmount} coins added to user ${uid}`);
      result.success = true;
      return result;
    } catch (error) {
      console.error("Error updating data for login reward:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error adding daily login reward:", error);
    return { success: false, error: error.message };
  }
};

// Calculate reward amount based on streak
const calculateStreakReward = (streak) => {
  // Base reward: 200 coins
  // Increase by 50 coins for each day up to 7 days
  
  let reward = 200;
  
  // Streak bonuses (caps at 30 days)
  const cappedStreak = Math.min(streak, 30);
  
  // Add 50 coins for each day of streak (up to day 7)
  if (cappedStreak < 7) {
    reward += (cappedStreak * 50);
  } else {
    // For streaks of 7 or more, add the maximum daily bonus
    reward += (7 * 50);
  }
  
  // Special milestone bonuses removed
  
  return reward;
};

// Get user's current login streak
export const getLoginStreak = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        currentStreak: userData.currentStreak || 0,
        highestStreak: userData.highestStreak || 0,
        lastLoginDate: userData.lastLoginDate || null
      };
    } else {
      return { currentStreak: 0, highestStreak: 0, lastLoginDate: null };
    }
  } catch (error) {
    console.error("Error getting login streak:", error);
    return { currentStreak: 0, highestStreak: 0, lastLoginDate: null };
  }
};

// Update user's login streak
export const updateLoginStreak = async (uid, currentStreak, highestStreak) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      currentStreak,
      highestStreak,
      lastLoginDate: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error("Error updating login streak:", error);
    return false;
  }
};

// Check if a user is eligible to claim daily reward
export const checkDailyRewardEligibility = async (uid) => {
  try {
    // For brand new accounts, always allow claiming
    // Check if the user exists first
    try {
      const userRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userRef);
      
      // If this is a brand new user, they should be eligible
      if (!userDoc.exists()) {
        console.log(`New user ${uid} is eligible for first daily login reward`);
        return true;
      }
    } catch (error) {
      console.error("Error checking user existence:", error);
      return false;
    }
    
    // Check for any login reward transactions from today
    try {
      const transactionsRef = collection(db, 'users', uid, 'transactions');
      
      // Get the start of today
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      const rewardQuery = query(
        transactionsRef,
        where('type', '==', 'login_reward'),
        orderBy('timestamp', 'desc'),
        limitFirestore(1) 
      );
      
      const rewardSnapshot = await getDocs(rewardQuery);
      
      // If there are no rewards claimed ever, user is eligible
      if (rewardSnapshot.empty) {
        return true;
      }
      
      // Check if the most recent claim was before today
      const lastReward = rewardSnapshot.docs[0].data();
      if (!lastReward.timestamp) {
        return true; // No timestamp, should be eligible
      }
      
      const lastClaimTime = lastReward.timestamp.toDate();
      
      // If the last claim was before today, user is eligible
      const isEligible = lastClaimTime < todayStart;
      
      // Try to check and reset streak, but don't let it fail the entire function
      try {
        await checkAndResetStreak(uid, lastClaimTime);
      } catch (error) {
        console.error("Error in checkAndResetStreak, continuing anyway:", error);
      }
      
      return isEligible;
    } catch (error) {
      console.error("Error checking transactions for daily reward eligibility:", error);
      return false;
    }
  } catch (error) {
    console.error("Error checking daily reward eligibility:", error);
    return false;
  }
};

// Check and reset streak if needed
export const checkAndResetStreak = async (uid, lastClaimTime) => {
  try {
    // Get the user's current streak data
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // No user data yet, nothing to reset
      return;
    }
    
    const userData = userDoc.data();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // If the last claim's date was yesterday, streak continues
    // If it was any earlier, reset streak
    const lastClaimDate = lastClaimTime;
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Format dates to compare just year, month, and day
    const yesterdayFormatted = new Date(
      yesterday.getFullYear(),
      yesterday.getMonth(),
      yesterday.getDate()
    ).getTime();
    
    const lastClaimFormatted = new Date(
      lastClaimDate.getFullYear(),
      lastClaimDate.getMonth(),
      lastClaimDate.getDate()
    ).getTime();
    
    // If last claim was before yesterday, reset streak
    if (lastClaimFormatted < yesterdayFormatted) {
      console.log(`Resetting streak for user ${uid} - Last claim: ${lastClaimDate}, Yesterday: ${yesterday}`);
      
      // Reset streak but keep track of highest streak
      await updateDoc(userRef, {
        currentStreak: 0,
        highestStreak: userData.highestStreak || 0,
        lastLoginDate: serverTimestamp()
      });
    }
  } catch (error) {
    console.error("Error checking/resetting streak:", error);
    return false;
  }
};

// Daily Code Service Functions

// Check if daily code exists
export const getDailyCode = async () => {
  try {
    const codeRef = doc(db, 'settings', 'dailyCode');
    const codeDoc = await getDoc(codeRef);
    
    if (codeDoc.exists()) {
      return codeDoc.data();
    }
    
    return null;
  } catch (error) {
    console.error("Error getting daily code:", error);
    return null;
  }
};

// Set daily code (admin only)
export const setDailyCode = async (code) => {
  try {
    const codeRef = doc(db, 'settings', 'dailyCode');
    await setDoc(codeRef, {
      code: code,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error("Error setting daily code:", error);
    return false;
  }
};

// Check if user has already claimed daily code
export const hasClaimedDailyCode = async (uid) => {
  try {
    // Get the start of today
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Check transactions for today
    const transactionsRef = collection(db, 'users', uid, 'transactions');
    const q = query(
      transactionsRef,
      where('type', '==', 'daily_code_reward'),
      where('timestamp', '>=', Timestamp.fromDate(todayStart)),
      limitFirestore(1)
    );
    
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error("Error checking if user claimed daily code:", error);
    return false;
  }
};

// Claim daily code reward
export const claimDailyCodeReward = async (uid, enteredCode) => {
  try {
    // Check if user has already claimed today
    const alreadyClaimed = await hasClaimedDailyCode(uid);
    if (alreadyClaimed) {
      return { success: false, message: "You've already claimed the daily code reward today." };
    }
    
    // Get the current daily code
    const dailyCode = await getDailyCode();
    if (!dailyCode || !dailyCode.code) {
      return { success: false, message: "No daily code is available right now." };
    }
    
    // Check if the entered code matches
    if (enteredCode !== dailyCode.code) {
      return { success: false, message: "Incorrect code. Please try again." };
    }
    
    // Define reward amount
    const rewardAmount = 1000;
    
    // Add coins to user's wallet
    await updateWalletBalance(uid, 'walletA', rewardAmount);
    
    // Update login source balance
    await updateSourceBalance(uid, 'login', rewardAmount);
    
    // Create transaction record
    await createTransaction(uid, 'daily_code_reward', rewardAmount, 'login');
    
    return { 
      success: true, 
      message: "Daily code redeemed successfully!", 
      amount: rewardAmount 
    };
  } catch (error) {
    console.error("Error claiming daily code reward:", error);
    return { success: false, message: "An error occurred. Please try again later." };
  }
};